/**
 * Billing Store Module
 * Gestiona el estado de suscripciones, planes y facturas
 */

import axios from '@/axios';

const API_ROOT = process.env.VUE_APP_API_BASE_URL || 'http://localhost:8000';
const API_BASE = `${API_ROOT}/api/billing`;

const normalizeListResponse = (payload) => {
  if (Array.isArray(payload)) return payload.filter(Boolean);
  if (payload && Array.isArray(payload.results)) return payload.results.filter(Boolean);
  return [];
};

const state = {
  plans: [],
  subscription: null,
  invoices: [],
  payments: [],
  loading: false,
  error: null,
  selectedPlan: null,
};

const getters = {
  hasActiveSubscription: (state) => !!state.subscription && state.subscription.status === 'active',
  nextRenewal: (state) => state.subscription?.renewal_date,
  currentPlan: (state) => state.subscription?.plan,
  plansLoaded: (state) => state.plans.length > 0,
  isTrialActive: (state) => !!state.subscription && state.subscription.status === 'trial' && state.subscription.is_trial_active,
  isTrialExpired: (state) => state.subscription?.status === 'trial_expired',
  trialRemainingDays: (state) => state.subscription?.trial_remaining_days || 0,
  canAccessSystem: (state) => {
    const sub = state.subscription;
    if (!sub) return false;
    if (sub.status === 'active') {
      return !sub.renewal_date || new Date(sub.renewal_date) >= new Date();
    }
    if (sub.status === 'trial' && sub.is_trial_active) return true;
    return false;
  },
};

const mutations = {
  setPlans(state, plans) {
    state.plans = normalizeListResponse(plans);
  },
  setSubscription(state, subscription) {
    state.subscription = subscription;
  },
  setInvoices(state, invoices) {
    state.invoices = normalizeListResponse(invoices);
  },
  setPayments(state, payments) {
    state.payments = normalizeListResponse(payments);
  },
  setLoading(state, loading) {
    state.loading = loading;
  },
  setError(state, error) {
    state.error = error;
  },
  setSelectedPlan(state, plan) {
    state.selectedPlan = plan;
  },
  clearSubscription(state) {
    state.subscription = null;
  },
};

const actions = {
  /**
   * Cargar planes disponibles
   */
  async fetchPlans({ commit }) {
    commit('setLoading', true);
    commit('setError', null);
    try {
      const response = await axios.get(`${API_BASE}/plans/`);
      commit('setPlans', response.data);
      return response.data;
    } catch (error) {
      commit('setError', error.message);
      throw error;
    } finally {
      commit('setLoading', false);
    }
  },

  /**
   * Cargar suscripción del usuario actual
   */
  async fetchSubscription({ commit }) {
    commit('setLoading', true);
    commit('setError', null);
    try {
      const response = await axios.get(`${API_BASE}/subscriptions/me/`);
      commit('setSubscription', response.data);
    } catch (error) {
      if (error.response?.status === 404) {
        commit('setSubscription', null);
      } else {
        commit('setError', error.message);
      }
    } finally {
      commit('setLoading', false);
    }
  },

  /**
   * Cargar facturas
   */
  async fetchInvoices({ commit }) {
    commit('setLoading', true);
    commit('setError', null);
    try {
      const response = await axios.get(`${API_BASE}/invoices/`);
      commit('setInvoices', response.data);
    } catch (error) {
      commit('setError', error.message);
    } finally {
      commit('setLoading', false);
    }
  },

  /**
   * Cargar pagos
   */
  async fetchPayments({ commit }) {
    commit('setLoading', true);
    commit('setError', null);
    try {
      const response = await axios.get(`${API_BASE}/payments/`);
      commit('setPayments', response.data);
    } catch (error) {
      commit('setError', error.message);
    } finally {
      commit('setLoading', false);
    }
  },

  /**
   * Crear nueva suscripción
   */
  async subscribe({ commit }, { planId, paymentMethodId }) {
    commit('setLoading', true);
    commit('setError', null);
    try {
      const response = await axios.post(
        `${API_BASE}/subscriptions/subscribe/`,
        {
          plan_id: planId,
          payment_method_id: paymentMethodId,
        }
      );
      commit('setSubscription', response.data);
      return response.data;
    } catch (error) {
      commit('setError', error.response?.data?.error || error.message);
      throw error;
    } finally {
      commit('setLoading', false);
    }
  },

  /**
   * Cambiar plan
   */
  async changePlan({ commit }, planId) {
    commit('setLoading', true);
    commit('setError', null);
    try {
      const response = await axios.patch(
        `${API_BASE}/subscriptions/change_plan/`,
        { plan_id: planId }
      );
      commit('setSubscription', response.data);
      return response.data;
    } catch (error) {
      commit('setError', error.response?.data?.error || error.message);
      throw error;
    } finally {
      commit('setLoading', false);
    }
  },

  /**
   * Cancelar suscripción
   */
  async cancelSubscription({ commit }) {
    commit('setLoading', true);
    commit('setError', null);
    try {
      const response = await axios.post(`${API_BASE}/subscriptions/cancel/`);
      commit('setSubscription', response.data);
      return response.data;
    } catch (error) {
      commit('setError', error.response?.data?.error || error.message);
      throw error;
    } finally {
      commit('setLoading', false);
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
